import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';

// This component will display vertical dots for navigation
const ScrollDots = () => {
  const [activeSection, setActiveSection] = useState(0);
  
  // Use useMemo to prevent recreating the array on each render
  const sections = useMemo(() => [
    { id: 'hero', label: 'Home' },
    { id: 'intro', label: 'About' },
    { id: 'locations', label: 'Locations' },
    { id: 'catering', label: 'Catering' },
    { id: 'tapas', label: 'Tapas' },
    { id: 'social', label: 'Gallery' }
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      // Find which section is currently in view
      const viewportHeight = window.innerHeight;
      const sectionElements = sections.map(section => document.getElementById(section.id));
      
      const activeSectionIndex = sectionElements.findIndex((element, index) => {
        if (!element) return false;
        
        const rect = element.getBoundingClientRect();
        const sectionTop = rect.top + scrollPosition;
        const sectionBottom = rect.bottom + scrollPosition;
        
        // If it's the first section, check if we're at the top
        if (index === 0) {
          return scrollPosition < sectionBottom - viewportHeight / 2;
        }
        
        // For all other sections
        return (
          scrollPosition >= sectionTop - viewportHeight / 2 &&
          scrollPosition < sectionBottom - viewportHeight / 2
        );
      });
      
      if (activeSectionIndex !== -1) {
        setActiveSection(activeSectionIndex);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);
  
  const scrollToSection = (index: number) => {
    const section = document.getElementById(sections[index].id);
    if (section) {
      window.scrollTo({
        top: section.offsetTop,
        behavior: 'smooth'
      });
    }
  };
  
  return (
    <div className="flex flex-col space-y-3">
      {sections.map((section, index) => (
        <motion.button
          key={section.id}
          onClick={() => scrollToSection(index)}
          className={`w-3 h-3 rounded-full ${
            activeSection === index ? 'bg-primary' : 'bg-secondary/30'
          }`}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9 }}
          animate={{ 
            scale: activeSection === index ? 1.2 : 1,
            backgroundColor: activeSection === index ? '#d4b13e' : 'rgba(139, 69, 19, 0.3)'
          }}
          transition={{ duration: 0.2 }}
          aria-label={`Scroll to ${section.label} section`}
          title={section.label}
        />
      ))}
    </div>
  );
};

export default ScrollDots; 